import {
  AdjustmentMode,
  BoundOperator,
  HigherSpecEffect,
  PercentageBasis,
  QualityFormulaType,
  RangeRowIssueType,
  SpecType,
} from '../types';

// ── Formula type classification ──────────────────────────────────────────────

const QUANTITY_FORMULA_TYPES = new Set<QualityFormulaType>([
  QualityFormulaType.QuantityAdjustedByPercent,
  QualityFormulaType.QuantityAdjustedToContracted,
  QualityFormulaType.QuantityAdjustedToDry,
]);

/**
 * Whether a formula type adjusts invoice quantity rather than unit price — shared by the API
 * (`QualityAdjustmentManager`) and the shared request schema (`CalculateAdjustmentsRequestSchema`,
 * which rejects more than one active quantity-type spec per request — a shipment has at most one
 * quantity adjustment, Moisture or Nitrogen, never both — see that schema's own comment) so
 * neither can drift apart.
 */
export const isQuantityFormulaType = (
  formulaType: QualityFormulaType,
): boolean => QUANTITY_FORMULA_TYPES.has(formulaType);

// ── Spec type metadata ──────────────────────────────────────────────────────

/**
 * Derives whether a higher actual value is a discount or a premium, and which percentage
 * basis a spec normalizes against, purely from the quality attribute — callers never provide
 * these (Premium/Discount and basis are a fixed design decision, not request fields). Shared
 * by the API (server-side calculation) and the UI (client-side labeling/preview) so neither
 * side can drift from the other.
 */
const HIGHER_SPEC_EFFECT_BY_SPEC_TYPE: Record<SpecType, HigherSpecEffect> = {
  [SpecType.Ash]: HigherSpecEffect.Discount,
  [SpecType.Btu]: HigherSpecEffect.Premium,
  [SpecType.Hgi]: HigherSpecEffect.Premium,
  [SpecType.Kcal]: HigherSpecEffect.Premium,
  [SpecType.Moisture]: HigherSpecEffect.Discount,
  // Nitrogen only ever uses QuantityAdjustedByPercent, which never reads this table — the
  // entry only exists to satisfy Record<SpecType, ...>'s exhaustiveness.
  [SpecType.Nitrogen]: HigherSpecEffect.Discount,
  [SpecType.Sulfur]: HigherSpecEffect.Discount,
  [SpecType.Volatile]: HigherSpecEffect.Discount,
};

/**
 * Percentage-point specs (composition %) normalize against a fixed 100; absolute-unit
 * specs (energy/quality index values) normalize relative to the contracted spec.
 */
// specs (energy/quality index values) normalize relative to the contracted spec.
const PERCENTAGE_BASIS_BY_SPEC_TYPE: Record<SpecType, PercentageBasis> = {
  [SpecType.Ash]: PercentageBasis.FixedHundred,
  [SpecType.Btu]: PercentageBasis.RelativeToContracted,
  [SpecType.Hgi]: PercentageBasis.RelativeToContracted,
  [SpecType.Kcal]: PercentageBasis.RelativeToContracted,
  [SpecType.Moisture]: PercentageBasis.FixedHundred,
  // Nitrogen only ever uses QuantityAdjustedByPercent, which never reads this table — the
  // entry only exists to satisfy Record<SpecType, ...>'s exhaustiveness.
  [SpecType.Nitrogen]: PercentageBasis.FixedHundred,
  [SpecType.Sulfur]: PercentageBasis.FixedHundred,
  [SpecType.Volatile]: PercentageBasis.FixedHundred,
};

/** Derives whether a higher actual value is a discount or a premium for the given spec type. */
export const getHigherSpecEffect = (specType: SpecType): HigherSpecEffect =>
  HIGHER_SPEC_EFFECT_BY_SPEC_TYPE[specType];

/** Derives which percentage basis (fixed-100 vs relative-to-contracted) a spec type normalizes against. */
export const getPercentageBasis = (specType: SpecType): PercentageBasis =>
  PERCENTAGE_BASIS_BY_SPEC_TYPE[specType];

/**
 * Which formula types a given spec type may configure — e.g. Nitrogen only ever uses
 * `QuantityAdjustedByPercent`, Moisture is the only spec type with a quantity-reducing or
 * full-price-recalculation option. Shared by the UI (filtering the formula-type selector) and
 * the shared spec/request schemas (`QualitySpecSchema`/`CalculateAdjustmentsRequestSchema`,
 * which reject a `specType`/`formulaType` combination outside this list) so the API can never
 * accept, directly or via a saved template, a combination the UI would never let a user build.
 */
const ALLOWED_FORMULA_TYPES_BY_SPEC_TYPE: Record<
  SpecType,
  QualityFormulaType[]
> = {
  [SpecType.Ash]: [QualityFormulaType.Percentage, QualityFormulaType.PerUnit],
  [SpecType.Btu]: [QualityFormulaType.Percentage, QualityFormulaType.PerUnit],
  [SpecType.Hgi]: [QualityFormulaType.Percentage, QualityFormulaType.PerUnit],
  [SpecType.Kcal]: [QualityFormulaType.Percentage, QualityFormulaType.PerUnit],
  [SpecType.Moisture]: [
    QualityFormulaType.Percentage,
    QualityFormulaType.PerUnit,
    QualityFormulaType.AdjustedSalesPrice,
    QualityFormulaType.QuantityAdjustedToContracted,
    QualityFormulaType.QuantityAdjustedToDry,
  ],
  [SpecType.Nitrogen]: [QualityFormulaType.QuantityAdjustedByPercent],
  [SpecType.Sulfur]: [
    QualityFormulaType.Percentage,
    QualityFormulaType.PerUnit,
  ],
  [SpecType.Volatile]: [
    QualityFormulaType.Percentage,
    QualityFormulaType.PerUnit,
  ],
};

/** The formula types allowed for a given spec type — see `ALLOWED_FORMULA_TYPES_BY_SPEC_TYPE`. */
export const getAllowedFormulaTypes = (
  specType: SpecType,
): QualityFormulaType[] => ALLOWED_FORMULA_TYPES_BY_SPEC_TYPE[specType];

/**
 * Whether `formulaType` is one of the formula types `specType` may configure. Takes a plain
 * `string` (not `SpecType`) because the persisted spec schema's own `specType` field is
 * unconstrained — an unrecognized `specType` is a separate, pre-existing validation gap this
 * check doesn't attempt to close, so it's treated as "no restriction" rather than thrown on.
 */
export const isFormulaTypeAllowedForSpecType = (
  specType: string,
  formulaType: QualityFormulaType,
): boolean =>
  // Pre-existing cross-package enum-identity lint quirk under this monorepo's typed-lint project
  // resolution (also hit by several ported UI files under services/ui/src/pages) — tsc itself has
  // no complaint here; `@typescript-eslint/no-unsafe-enum-assignment` just can't tell these two
  // resolutions of `SpecType` apart. See services/ui/src/api/pricingEngineApi.ts for more detail.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
  !Object.values(SpecType).includes(specType as SpecType) ||
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
  getAllowedFormulaTypes(specType as SpecType).includes(formulaType);

/**
 * The Discount/Premium value a spec type's HigherSpecEffect maps directly onto. HigherSpecEffect
 * (a spec-type-level fact: does a higher actual value mean Discount or Premium for this spec?)
 * and AdjustmentMode (a row-level field: which side does this row apply to?) share the same two
 * values by design, but are deliberately separate enums — this is the one explicit place that
 * bridges from the former into the latter.
 */
const HIGHER_SPEC_EFFECT_TO_ADJUSTMENT_MODE: Record<
  HigherSpecEffect,
  AdjustmentMode
> = {
  [HigherSpecEffect.Discount]: AdjustmentMode.Discount,
  [HigherSpecEffect.Premium]: AdjustmentMode.Premium,
};

/**
 * Derives a spec type's default AdjustmentMode from its HigherSpecEffect — this
 * doubles as a Threshold spec's default (and only mandatory) side, e.g. Moisture is naturally
 * Discount, so a fresh Threshold spec starts Discount-only.
 */
export const getDefaultAdjustmentModeFromSpecType = (
  specType: SpecType,
): AdjustmentMode =>
  HIGHER_SPEC_EFFECT_TO_ADJUSTMENT_MODE[getHigherSpecEffect(specType)];

// ── Signed diff orientation ──────────────────────────────────────────────────
// Orients the raw spec diff so negative always means discount and positive always means
// premium, regardless of which raw direction (actual > contracted, or actual < contracted)
// is the discount side for this spec type.

/** Signed diff between `actualSpec` and `contractedSpec`, oriented negative=discount/positive=premium. */
export const getSignedSpecDiff = (
  actualSpec: number,
  contractedSpec: number,
  higherSpecEffect: HigherSpecEffect,
): number => {
  const rawDiff = actualSpec - contractedSpec;
  return higherSpecEffect === HigherSpecEffect.Discount ? -rawDiff : rawDiff;
};

// ── Row/side matching ────────────────────────────────────────────────────────

/**
 * The subset of a formula row's fields needed to select it. Shared by `Percentage` and
 * `PerUnit` Range rows.
 */
// `PerUnit` Range rows — the contracted spec they're measured against is a sibling field on
// the enclosing input, not derived from these bounds.
export interface IBoundedFormulaRow {
  /** Row-selection floor. */
  lowerBound: number;
  /** Whether `lowerBound` itself counts as a match — defaults to `Exclusive`. */
  lowerBoundOperator?: BoundOperator;
  /** Row-selection ceiling, or `null` for no ceiling. */
  upperBound: null | number;
  /** Whether `upperBound` itself counts as a match — defaults to `Inclusive`. */
  upperBoundOperator?: BoundOperator;
}

/**
 * Finds the index of the Range row whose range contains `actualSpec`, respecting each bound's
 * own inclusive/exclusive operator (real legacy formulas mix both — some rows are exclusive on
 * both sides, leaving an intentional gap at their shared boundary value). A row's operators
 * default to `Exclusive` (lower) / `Inclusive` (upper) when omitted. `upperBound === null` means
 * no ceiling. Returns `-1` when no row's range covers `actualSpec` — callers treat this as
 * "no adjustment applies".
 */
export const findApplicableRowIndex = (
  rows: IBoundedFormulaRow[],
  actualSpec: number,
): number =>
  rows.findIndex(row => {
    const matchesLower =
      row.lowerBoundOperator === BoundOperator.Inclusive
        ? actualSpec >= row.lowerBound
        : actualSpec > row.lowerBound;
    const matchesUpper =
      row.upperBound === null ||
      (row.upperBoundOperator === BoundOperator.Exclusive
        ? actualSpec < row.upperBound
        : actualSpec <= row.upperBound);
    return matchesLower && matchesUpper;
  });

/** One adjacent-pair issue — `rowIndex` is the earlier (lower-order) row's index within `rows`. */
export interface IRangeRowIssue {
  rowIndex: number;
  type: RangeRowIssueType;
}

/** Classifies one adjacent pair — `null` means a clean handoff, nothing to report. */
const classifyRowPair = (
  upperRow: IBoundedFormulaRow,
  lowerRow: IBoundedFormulaRow,
): null | RangeRowIssueType => {
  if (lowerRow.lowerBound < upperRow.lowerBound)
    return RangeRowIssueType.Unsorted;
  if (upperRow.upperBound === null) return null;

  if (lowerRow.lowerBound < upperRow.upperBound)
    return RangeRowIssueType.Overlap;
  if (lowerRow.lowerBound > upperRow.upperBound) return RangeRowIssueType.Gap;
  if (
    upperRow.upperBoundOperator === BoundOperator.Inclusive &&
    lowerRow.lowerBoundOperator === BoundOperator.Inclusive
  ) {
    return RangeRowIssueType.Overlap;
  }
  if (
    upperRow.upperBoundOperator === BoundOperator.Exclusive &&
    lowerRow.lowerBoundOperator === BoundOperator.Exclusive
  ) {
    return RangeRowIssueType.Gap;
  }
  return null;
};

/**
 * Classifies every adjacent pair of a Range formula's rows as a clean handoff (no issue), a gap
 * (no row covers that interval — valid, intentional "no adjustment applies" business data), an
 * overlap (two rows both claim the same value — `findApplicableRowIndex`'s first-match scan would
 * then resolve the tie by array position alone), or out of ascending-`lowerBound` order (the same
 * ambiguity as an overlap, since nothing then guarantees adjacent array entries are adjacent
 * value ranges). Shared by the persisted/calculate-endpoint schemas (which reject `overlap` and
 * `unsorted`) and the UI (which also surfaces `gap` as a non-blocking heads-up) so both agree on
 * the exact same relation between rows.
 */
export const findRangeRowIssues = (
  rows: IBoundedFormulaRow[],
): IRangeRowIssue[] =>
  rows.slice(0, -1).flatMap((upperRow, rowIndex) => {
    const type = classifyRowPair(upperRow, rows[rowIndex + 1]);
    return type === null ? [] : [{ rowIndex, type }];
  });

/**
 * Finds the index of the Threshold side that applies to `actualSpec`, with no bounds involved
 * at all — a side applies purely based on which direction (vs. `contractedSpec`) its
 * `adjustmentMode` represents, oriented by the spec type's natural higher-value effect. Returns
 * `-1` when `actualSpec` sits exactly at `contractedSpec` (matches neither side) or the matching
 * side wasn't configured — callers treat this as "no adjustment applies".
 */
export const findApplicableThresholdSideIndex = (
  sides: Array<{ adjustmentMode: AdjustmentMode }>,
  actualSpec: number,
  contractedSpec: number,
  higherSpecEffect: HigherSpecEffect,
): number => {
  const signedDiff = getSignedSpecDiff(
    actualSpec,
    contractedSpec,
    higherSpecEffect,
  );
  return sides.findIndex(side =>
    side.adjustmentMode === AdjustmentMode.Discount
      ? signedDiff < 0
      : signedDiff > 0,
  );
};
